import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Copy, Users, Euro, TrendingUp, Gift, Clock, CheckCircle, XCircle, AlertCircle, Wallet, Send, History } from 'lucide-react';
import { toast } from 'sonner';
import errMsg from '../utils/apiError';

export default function AmbassadorPage() {
  const [dashboard, setDashboard] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payoutForm, setPayoutForm] = useState({ amount: '', iban: '', holder_name: '' });
  const [submittingPayout, setSubmittingPayout] = useState(false);
  const navigate = useNavigate();
  const { api } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashboardRes, tiersRes] = await Promise.all([
        api.get('/ambassador/dashboard'),
        api.get('/ambassador/tiers'),
      ]);
      setDashboard(dashboardRes.data);
      setTiers(tiersRes.data);
    } catch (error) {
      console.error('Error fetching ambassador data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(dashboard?.referral_code || '');
    toast.success('Code copié !');
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(dashboard?.referral_link || '');
    toast.success('Lien copié !');
  };

  const openPayoutDialog = () => {
    const balance = dashboard?.available_balance || 0;
    if (balance < 50) {
      toast.error('Solde minimum de 50 € requis pour demander un virement');
      return;
    }
    setPayoutForm({ amount: balance.toFixed(2), iban: '', holder_name: '' });
    setPayoutOpen(true);
  };

  const submitPayout = async () => {
    const amount = parseFloat(payoutForm.amount);
    if (isNaN(amount) || amount < 50) {
      toast.error('Montant minimum : 50 €');
      return;
    }
    if (!payoutForm.iban || payoutForm.iban.replace(/\s/g, '').length < 15) {
      toast.error('IBAN invalide');
      return;
    }
    if (!payoutForm.holder_name.trim()) {
      toast.error('Nom du titulaire requis');
      return;
    }
    setSubmittingPayout(true);
    try {
      await api.post('/ambassador/payout', {
        amount,
        iban: payoutForm.iban.replace(/\s/g, ''),
        holder_name: payoutForm.holder_name.trim(),
      });
      toast.success(`💸 Demande de retrait de ${amount.toFixed(2)} € envoyée !`);
      setPayoutOpen(false);
      fetchData();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSubmittingPayout(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'validated': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <AlertCircle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'validated': return 'Validé';
      case 'pending': return 'En attente';
      case 'rejected': return 'Rejeté';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Users className="w-12 h-12 text-primary animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="ambassador-page">
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Programme Ambassadeur</h1>
            <p className="text-muted-foreground">Parrainez et gagnez des commissions</p>
          </div>
        </div>

        {/* Important Notice */}
        <Card className="mb-6 border-blue-500/30 bg-blue-500/10">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Gift className="w-5 h-5 text-blue-500 mt-0.5" />
              <div className="text-sm">
                <strong>Comment ça marche ?</strong>
                <ol className="mt-2 space-y-1 text-muted-foreground">
                  <li>1. Partagez votre lien Calendly personnalisé</li>
                  <li>2. Votre prospect prend RDV et rejoint Odys</li>
                  <li>3. Votre commission est créditée automatiquement !</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-6 text-center">
              <Users className="w-8 h-8 mx-auto mb-2 text-primary" />
              <p className="text-3xl font-bold">{dashboard?.validated_referrals || 0}</p>
              <p className="text-sm text-muted-foreground">Filleuls validés</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <Clock className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
              <p className="text-3xl font-bold">{dashboard?.pending_referrals || 0}</p>
              <p className="text-sm text-muted-foreground">En attente</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30">
            <CardContent className="p-6 text-center">
              <Euro className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <p className="text-3xl font-bold text-green-500">{dashboard?.total_commission?.toFixed(2) || '0.00'}€</p>
              <p className="text-sm text-muted-foreground">Commission totale</p>
            </CardContent>
          </Card>
        </div>

        {/* Portefeuille (Wallet) */}
        <Card className="mb-6 border-primary/40 bg-gradient-to-br from-primary/5 to-yellow-500/5" data-testid="ambassador-wallet">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              Mon Portefeuille
            </CardTitle>
            <CardDescription>Suivez vos gains et demandez un virement dès 50 €</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div className="bg-card border border-primary/30 rounded-lg p-4 text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Solde disponible</p>
                <p className="text-3xl font-bold text-primary" data-testid="wallet-balance">
                  {(dashboard?.available_balance || 0).toFixed(2)} €
                </p>
              </div>
              <div className="bg-card border border-yellow-500/30 rounded-lg p-4 text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">En cours de paiement</p>
                <p className="text-2xl font-bold text-yellow-600" data-testid="wallet-pending">
                  {(dashboard?.pending_payout || 0).toFixed(2)} €
                </p>
              </div>
              <div className="bg-card border border-green-500/30 rounded-lg p-4 text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Déjà encaissé</p>
                <p className="text-2xl font-bold text-green-600" data-testid="wallet-paid">
                  {(dashboard?.total_paid || 0).toFixed(2)} €
                </p>
              </div>
            </div>
            <Button
              onClick={openPayoutDialog}
              disabled={(dashboard?.available_balance || 0) < 50}
              className="w-full bg-primary hover:bg-primary/90"
              data-testid="request-payout-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {(dashboard?.available_balance || 0) < 50
                ? `Minimum 50 € (encore ${(50 - (dashboard?.available_balance || 0)).toFixed(2)} €)`
                : 'Demander un virement'}
            </Button>
          </CardContent>
        </Card>

        {/* Current Tier */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Votre Palier
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-2xl font-bold text-primary">{dashboard?.current_tier?.tier_name || 'Non qualifié'}</p>
                <p className="text-muted-foreground">
                  Commission : <span className="font-semibold text-green-500">{dashboard?.current_tier?.commission_percent || 5}%</span>
                </p>
              </div>
              {dashboard?.next_tier && (
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Prochain palier</p>
                  <p className="font-semibold">{dashboard.next_tier.tier_name}</p>
                  <p className="text-xs text-muted-foreground">
                    Encore {dashboard.referrals_to_next_tier} filleul(s)
                  </p>
                </div>
              )}
            </div>
            
            {/* Tiers Progress */}
            <div className="space-y-2">
              {tiers.map((tier, index) => (
                <div key={tier.id} className="flex items-center gap-3">
                  <Badge 
                    variant={dashboard?.validated_referrals >= tier.min_referrals ? 'default' : 'secondary'}
                    className="w-24"
                  >
                    {tier.tier_name}
                  </Badge>
                  <span className="text-sm text-muted-foreground">{tier.min_referrals}+ filleuls</span>
                  <span className="text-sm font-semibold text-green-500 ml-auto">{tier.commission_percent}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Referral Code */}
        <Card className="mb-6 border-primary/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-primary" />
              Votre Lien Ambassadeur
            </CardTitle>
            <CardDescription>
              Partagez ce lien Calendly personnalisé avec vos contacts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Lien de prise de RDV (Calendly)</label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 bg-primary/10 border border-primary/30 rounded-lg p-3 text-sm truncate font-medium">
                    {dashboard?.referral_link}
                  </div>
                  <Button onClick={copyReferralLink} className="bg-primary hover:bg-primary/90">
                    <Copy className="w-4 h-4 mr-2" />
                    Copier
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Ce lien redirige vers Calendly avec votre code ambassadeur tracké automatiquement.
                </p>
              </div>
              
              <div className="border-t pt-4">
                <label className="text-sm text-muted-foreground">Votre code ambassadeur</label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="bg-secondary/50 rounded-lg px-4 py-2 font-mono text-lg font-bold tracking-wider">
                    {dashboard?.referral_code}
                  </div>
                  <Button variant="outline" size="sm" onClick={copyReferralCode}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Commission Calculator */}
              <div className="border-t pt-4">
                <label className="text-sm text-muted-foreground mb-3 block">Simulateur de commission</label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-secondary/30 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Odys'Start (2000€)</p>
                    <p className="text-lg font-bold text-green-500">
                      +{((2000 * (dashboard?.current_tier?.commission_percent || 5)) / 100).toFixed(0)}€
                    </p>
                  </div>
                  <div className="bg-secondary/30 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Odys'Mentoring (6000€)</p>
                    <p className="text-lg font-bold text-green-500">
                      +{((6000 * (dashboard?.current_tier?.commission_percent || 5)) / 100).toFixed(0)}€
                    </p>
                  </div>
                </div>
                <p className="text-xs text-center text-muted-foreground mt-2">
                  Avec votre taux actuel de {dashboard?.current_tier?.commission_percent || 5}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referrals List */}
        <Card>
          <CardHeader>
            <CardTitle>Vos Filleuls</CardTitle>
            <CardDescription>Historique de vos parrainages et commissions</CardDescription>
          </CardHeader>
          <CardContent>
            {dashboard?.referrals?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.referrals.map((referral) => (
                  <div 
                    key={referral.id} 
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      referral.status === 'validated' 
                        ? 'bg-green-500/10 border-green-500/30' 
                        : 'bg-secondary/30 border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(referral.status)}
                      <div>
                        <p className="font-medium">{referral.referee_email}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{new Date(referral.created_at).toLocaleDateString('fr-FR')}</span>
                          {referral.product && (
                            <>
                              <span>•</span>
                              <Badge variant="outline" className="text-xs">
                                {referral.product === 'mentoring' ? "Odys'Mentoring" : "Odys'Start"}
                              </Badge>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={referral.status === 'validated' ? 'default' : 'secondary'}>
                        {getStatusLabel(referral.status)}
                      </Badge>
                      {referral.commission_amount && (
                        <p className="text-sm font-bold text-green-500 mt-1">
                          +{referral.commission_amount.toFixed(2)}€
                          <span className="text-xs font-normal text-muted-foreground ml-1">
                            ({referral.commission_percent}%)
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucun filleul pour le moment</p>
                <p className="text-sm mt-1">Partagez votre lien Calendly pour commencer !</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payouts History */}
        {dashboard?.payouts?.length > 0 && (
          <Card className="mt-6" data-testid="payouts-history">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Historique des retraits
              </CardTitle>
              <CardDescription>Suivi de vos demandes de virement</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard.payouts.map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-4 rounded-lg border bg-secondary/20">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(p.status === 'paid' ? 'validated' : p.status)}
                      <div>
                        <p className="font-medium">{p.amount.toFixed(2)} €</p>
                        <p className="text-xs text-muted-foreground">
                          IBAN se terminant par ••••{p.iban} — {new Date(p.requested_at).toLocaleDateString('fr-FR')}
                        </p>
                        {p.admin_note && (
                          <p className="text-xs text-muted-foreground italic mt-1">Note : {p.admin_note}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={p.status === 'paid' ? 'default' : p.status === 'rejected' ? 'destructive' : 'secondary'}>
                        {p.status === 'paid' ? 'Payé' : p.status === 'rejected' ? 'Rejeté' : 'En attente'}
                      </Badge>
                      {p.paid_at && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(p.paid_at).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Payout Request Dialog */}
      <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Demander un virement
            </DialogTitle>
            <DialogDescription>
              Solde disponible : <strong className="text-primary">{(dashboard?.available_balance || 0).toFixed(2)} €</strong>
              <br />
              Le virement est traité manuellement sous 5 jours ouvrés après validation admin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="payout-amount">Montant (€) *</Label>
              <Input
                id="payout-amount"
                type="number"
                min="50"
                step="0.01"
                max={dashboard?.available_balance || 0}
                value={payoutForm.amount}
                onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })}
                data-testid="payout-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payout-holder">Nom du titulaire du compte *</Label>
              <Input
                id="payout-holder"
                value={payoutForm.holder_name}
                onChange={(e) => setPayoutForm({ ...payoutForm, holder_name: e.target.value })}
                placeholder="Prénom NOM"
                data-testid="payout-holder-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payout-iban">IBAN *</Label>
              <Input
                id="payout-iban"
                value={payoutForm.iban}
                onChange={(e) => setPayoutForm({ ...payoutForm, iban: e.target.value.toUpperCase() })}
                placeholder="FR76 3000 6000 0112 3456 7890 189"
                className="font-mono"
                data-testid="payout-iban-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayoutOpen(false)} disabled={submittingPayout}>Annuler</Button>
            <Button
              onClick={submitPayout}
              disabled={submittingPayout}
              className="bg-primary hover:bg-primary/90"
              data-testid="confirm-payout-btn"
            >
              {submittingPayout ? 'Envoi...' : 'Confirmer la demande'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
